import { api } from "../lib/api";

/**
 * Ring the assigned agent's bell when a client is handed to them.
 *
 * Assignment used to be a bare `clients.update` with zero side effects, so an
 * agent's work started at a moment nothing told her about — she found new
 * clients by polling the list. This is the producer for that moment.
 *
 * Addressing: an agent sees a notification only when it carries
 * entity_type='client' plus an entity_id they own (see
 * _filter_notifications_for_user in backend routes/notifications.py). Because
 * the assignment write lands before this runs, the new agent already owns the
 * client and the row reaches exactly them — no recipient column needed.
 */
export interface AssignmentNotificationInput {
  clientId: string;
  /** Kept for callers; the SERVER derives the display name from the client row
   * rather than trusting a name supplied by the browser. */
  clientFirstName?: string;
  clientLastName?: string;
  /** The agent the client was just assigned TO. Null/empty means unassigned. */
  newAgentId: string | null | undefined;
  /** The agent the client was assigned to before this change. */
  previousAgentId: string | null | undefined;
  /** The signed-in user performing the assignment. */
  actingUserId: string | null | undefined;
}

export async function notifyClientAssigned({
  clientId,
  newAgentId,
  previousAgentId,
  actingUserId,
}: AssignmentNotificationInput): Promise<boolean> {
  // Clearing an assignment is not news for anyone: there is no new owner to
  // tell, and the old owner losing a client is a manager conversation.
  if (!newAgentId) return false;

  // Re-saving the form without changing the agent must not re-ring the bell.
  if (newAgentId === previousAgentId) return false;

  // Self-assignment is silent — she just did it, on this screen.
  if (actingUserId && newAgentId === actingUserId) return false;

  // Written by the SERVER, not here. `notifications` INSERT is admin-only in
  // this repo (002_fix_rls_policies.sql:131, re-affirmed by 022 "Notifications
  // stay admin-only") while 022 extended `clients` UPDATE to managers — so the
  // manager who actually performs assignments could make the change and then be
  // denied by RLS when writing the bell, silently, into a console.error.
  //
  // The endpoint also re-reads the assignment before announcing it. This code
  // used to fire on the caller's say-so, and the `clients.update` above it
  // discards its error (PostgREST returns {error} rather than throwing), so a
  // denied assignment still rang the bell. The server believes the row.
  try {
    const result = await api.post<{ notified: boolean }>(
      "/notifications/client-assigned",
      { client_id: clientId, new_agent_id: newAgentId }
    );
    return Boolean(result?.notified);
  } catch (error) {
    console.error("Assignment notification failed:", error);
    return false;
  }
}
